import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Form, Input, Select, Button } from 'antd';
import {
  UserOutlined, SaveOutlined, MailOutlined,
  CloseOutlined, SafetyOutlined, TeamOutlined, InfoCircleOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserService, rolesService, agencesService } from '../../common/services';
import { rulePhoneCI, ruleEmail, ruleRequired, normalizeUpper } from '../../common/validators';
import PermissionsSelector from '../../components/permissionsSelector';
import '../../components/capFormShell.scss';

const { Option } = Select;

const AjouterAdmin = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [agences, setAgences] = useState([]);
  const [editing, setEditing] = useState(false);
  const [selectedPerms, setSelectedPerms] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');

  useEffect(() => {
    let active = true;
    (async () => {
      const [rolesRes, agRes] = await Promise.all([
        rolesService.getAll().catch(() => []),
        agencesService.getAll().catch(() => []),
      ]);
      if (!active) return;
      const rolesList = Array.isArray(rolesRes) ? rolesRes : rolesRes?.data || [];
      setRoles(rolesList);
      setAgences(Array.isArray(agRes) ? agRes : agRes?.data || []);

      if (editId) {
        setEditing(true);
        try {
          const res = await UserService.getAdministrateurById(editId);
          const data = res?.data || res;
          if (!active) return;
          form.setFieldsValue({
            ...data,
            phone: data.phone?.replace('+225', ''),
            agence: data.agence?._id || data.agence,
          });
          const roleObj = rolesList.find((x) => x.name === data.role);
          const rolePerms = Array.isArray(roleObj?.permissions) ? roleObj.permissions : [];
          const userPerms = Array.isArray(data.permissions) ? data.permissions : [];
          setSelectedPerms(Array.from(new Set([...rolePerms, ...userPerms])));
        } catch {
          toast.error('Erreur lors du chargement de l\'administrateur');
        }
      }
    })();
    return () => { active = false; };
  }, [editId]);

  const handleRoleChange = (roleName) => {
    const r = roles.find((x) => x.name === roleName);
    setSelectedPerms(Array.isArray(r?.permissions) ? [...r.permissions] : []);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const data = { ...values, phone: `+225${values.phone}`, permissions: selectedPerms };
      if (editing) { await UserService.updateProfile(editId, data); toast.success('Administrateur mis à jour avec succès'); }
      else { await UserService.createAdmin(data); toast.success('Administrateur créé avec succès'); }
      navigate('/dashboard/gestion-admins');
    } catch (error) {
      toast.error(error.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-wrapper">
      <div className="role-form-page">
        {/* Header */}
        <header className="role-form-header">
          <span className="role-form-header-icon is-blue"><TeamOutlined /></span>
          <div>
            <h1 className="role-form-title">
              {editing ? 'Modifier l\'administrateur' : 'Nouvel administrateur'}
            </h1>
            <p className="role-form-subtitle">
              Renseignez l'identité, le rôle et les permissions spécifiques de ce compte.
            </p>
          </div>
        </header>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* Carte Identité */}
          <div className="role-form-card" style={{ marginBottom: 16 }}>
            <div className="role-form-card-head">
              <IdcardOutlined />
              <h2 className="role-form-card-title">Identité &amp; accès</h2>
            </div>
            <div className="role-form-card-body">
              <div className="role-form-fields">
                <Form.Item
                  name="firstName"
                  label={<span className="role-form-label">Prénom</span>}
                  rules={ruleRequired('prénom')}
                  normalize={normalizeUpper}
                >
                  <Input autoFocus prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} placeholder="Prénom" />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  label={<span className="role-form-label">Nom</span>}
                  rules={ruleRequired('nom')}
                  normalize={normalizeUpper}
                >
                  <Input placeholder="Nom" />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label={<span className="role-form-label">Téléphone</span>}
                  rules={rulePhoneCI({ label: 'téléphone' })}
                >
                  <Input addonBefore="+225" maxLength={10} placeholder="07 00 00 00 00" />
                </Form.Item>
                <Form.Item
                  name="email"
                  label={<span className="role-form-label">Email</span>}
                  rules={ruleEmail()}
                  normalize={(v) => v?.toLowerCase().trim()}
                >
                  <Input prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} placeholder="email@exemple.com" />
                </Form.Item>
                <Form.Item
                  name="role"
                  label={<span className="role-form-label">Rôle</span>}
                  rules={[{ required: true, message: 'Veuillez sélectionner un rôle' }]}
                >
                  <Select placeholder="Sélectionner un rôle" showSearch optionFilterProp="children" onChange={handleRoleChange}>
                    {roles.map((r) => (
                      <Option key={r._id || r.name} value={r.name}>{r.label || r.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="agence"
                  label={<span className="role-form-label">Agence</span>}
                >
                  <Select placeholder="Sélectionner une agence" allowClear showSearch optionFilterProp="children">
                    {agences.map((a) => (
                      <Option key={a._id} value={a._id}>{a.nom}</Option>
                    ))}
                  </Select>
                </Form.Item>

                {!editing && (
                  <div className="role-form-note">
                    <InfoCircleOutlined />
                    <span>
                      <strong>Mot de passe temporaire :</strong> un mot de passe sera communiqué à
                      l'utilisateur. Il devra le changer obligatoirement à sa première connexion.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Carte Permissions */}
          <div className="role-form-card">
            <div className="role-form-card-head">
              <SafetyOutlined />
              <h2 className="role-form-card-title">Permissions spécifiques</h2>
            </div>
            <PermissionsSelector value={selectedPerms} onChange={setSelectedPerms} />
          </div>

          {/* Barre d'action sticky */}
          <div className="role-form-footer">
            <div className="role-form-footer-recap">
              <SafetyOutlined />
              <span>Permissions sélectionnées</span>
              <span className="role-form-footer-badge">{selectedPerms.length}</span>
            </div>
            <div className="role-form-footer-actions">
              <Button
                icon={<CloseOutlined />}
                className="role-form-btn-cancel"
                onClick={() => navigate('/dashboard/gestion-admins')}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                className="role-form-btn-save"
              >
                {loading ? 'Enregistrement…' : editing ? 'Mettre à jour' : 'Créer l\'administrateur'}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AjouterAdmin;
